/**
 * Full text search, as an overlay.
 *
 * The rules it plays by:
 *
 * - It is keyboard first. `/` opens it, the arrows walk one flat list across both
 *   sections, Enter opens, Escape closes. A search you have to reach for with the
 *   mouse is a search you stop using by the second week.
 * - Two questions get two sections. "Which book was that" is answered by title and
 *   author over `/api/library`; "where was that sentence" is answered by FTS over
 *   `/api/search`. Mixing them into one ranked list answers neither well.
 * - The snippet arrives as raw chapter text with `<mark>` around the hit, and
 *   chapter text is not HTML: it can hold a stray `<` or `&` of its own. It is
 *   therefore parsed for the two mark tags and nothing else, and rendered as React
 *   nodes. `dangerouslySetInnerHTML` on this string would be an injection straight
 *   from the contents of a book.
 * - Requests race and get aborted. Only the newest query is allowed to write.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api } from "../lib/api";
import { useScrollLock } from "../lib/useScrollLock";
import { CloseIcon, SearchIcon } from "../components/icons";
import { Spinner } from "../components/ui";
import { href } from "../lib/route";
import { t, useUiLang } from "../i18n";
import type { BookSummary, DiscoverResult, SearchHit } from "../lib/types";
import { useDebounced } from "./useLibrary";

const BOOK_LIMIT = 6;
const PASSAGE_LIMIT = 24;
const CATALOGUE_LIMIT = 8;

/**
 * The catalogue search waits longer than the local one, and the gap is the
 * whole design of this panel.
 *
 * The local index answers in a millisecond off a file on the same disk. Three
 * public catalogues answer in one to eight seconds, over the network, and one
 * of them is a museum. Debouncing both at 200 ms would fire a round of remote
 * requests at every keystroke of a word the reader has not finished typing, to
 * show rows they will never read, and would rate limit the one source that
 * matters within a minute.
 *
 * So: local results appear as you type, and the catalogues are asked once you
 * have stopped.
 */
const CATALOGUE_DEBOUNCE = 700;

/**
 * Where a passage result sends you.
 *
 * `#/book/<id>?s=<sentence>` is the contract with the reader: it reads
 * `location.hash`, takes `s` as a book-wide sentence index and jumps there
 * instead of restoring the saved position. Keep this format byte for byte; the
 * reader parses it.
 */
export function passageHref(bookId: string, sentence: number | null): string {
  const base = href({ name: "book", id: bookId });
  return sentence === null || !Number.isFinite(sentence) ? base : `${base}?s=${sentence}`;
}

/**
 * Split a snippet on `<mark>` pairs. Everything outside a pair is literal text,
 * whatever it looks like, which is exactly the safety property we want.
 */
export function renderSnippet(raw: string): React.ReactNode[] {
  // Chapter text keeps the line breaks of the book. In a two line result row they
  // land in arbitrary places and make the snippet look torn, so runs of
  // whitespace collapse to one space before anything else happens.
  const snippet = raw.replace(/\s+/g, " ").trim();
  const nodes: React.ReactNode[] = [];
  const pattern = /<mark>([\s\S]*?)<\/mark>/g;
  let cursor = 0;
  let match = pattern.exec(snippet);
  let key = 0;
  while (match !== null) {
    if (match.index > cursor) nodes.push(snippet.slice(cursor, match.index));
    nodes.push(
      <mark
        key={`m${key}`}
        className="rounded-sm bg-[var(--color-accent-soft)] px-0.5 text-[var(--color-accent)]"
      >
        {match[1]}
      </mark>,
    );
    key += 1;
    cursor = match.index + match[0].length;
    match = pattern.exec(snippet);
  }
  if (cursor < snippet.length) nodes.push(snippet.slice(cursor));
  return nodes;
}

type Row =
  | { kind: "book"; book: BookSummary; target: string }
  | { kind: "hit"; hit: SearchHit; target: string }
  // A catalogue row has no target: pressing Enter on it starts an acquisition
  // rather than navigating anywhere. It is the one row in this panel that
  // changes the library instead of moving around it.
  | { kind: "find"; find: DiscoverResult };

interface Results {
  books: BookSummary[];
  hits: SearchHit[];
}

const NOTHING: Results = { books: [], hits: [] };

function useSearch(term: string, open: boolean): { results: Results; busy: boolean } {
  const [results, setResults] = useState<Results>(NOTHING);
  const [busy, setBusy] = useState(false);
  const run = useRef(0);

  useEffect(() => {
    if (!open || term.trim().length < 2) {
      setResults(NOTHING);
      setBusy(false);
      return;
    }
    const id = ++run.current;
    const controller = new AbortController();
    setBusy(true);

    Promise.all([
      api.library({ q: term, limit: BOOK_LIMIT, sort: "recent" }, controller.signal),
      api.search(term, { limit: PASSAGE_LIMIT }, controller.signal),
    ])
      .then(([page, found]) => {
        // A slower earlier query must never land on top of a faster later one.
        if (run.current !== id) return;
        setResults({ books: page.books, hits: found.hits });
      })
      .catch(() => {
        if (run.current !== id || controller.signal.aborted) return;
        setResults(NOTHING);
      })
      .finally(() => {
        if (run.current === id) setBusy(false);
      });

    return () => controller.abort();
  }, [term, open]);

  return { results, busy };
}

/**
 * What the open catalogues have that this shelf does not.
 *
 * Its own request, its own spinner, its own failure. A catalogue having a bad
 * day must not empty the local results, and the eight seconds it sometimes
 * takes must not be eight seconds of blank panel.
 */
function useDiscover(term: string, open: boolean): { found: DiscoverResult[]; busy: boolean } {
  const [found, setFound] = useState<DiscoverResult[]>([]);
  const [busy, setBusy] = useState(false);
  const run = useRef(0);

  useEffect(() => {
    if (!open || term.trim().length < 3) {
      setFound([]);
      setBusy(false);
      return;
    }
    const id = ++run.current;
    const controller = new AbortController();
    setBusy(true);
    api
      .discover(term, CATALOGUE_LIMIT, controller.signal)
      .then((page) => {
        if (run.current !== id) return;
        setFound(page.results);
      })
      .catch(() => {
        if (run.current !== id || controller.signal.aborted) return;
        setFound([]);
      })
      .finally(() => {
        if (run.current === id) setBusy(false);
      });
    return () => controller.abort();
  }, [term, open]);

  return { found, busy };
}

const SOURCE_LABEL: Record<string, "addSourceArchive" | "addSourceGutenberg"> = {
  archive: "addSourceArchive",
  gutenberg: "addSourceGutenberg",
};

/** A cover thumbnail that is allowed to fail, because half of these will. */
function Thumb({ src, alt }: { src: string; alt: string }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  if (!src || broken) {
    return (
      <span
        aria-hidden="true"
        className="h-11 w-8 shrink-0 rounded-sm bg-[var(--color-raised)] ring-1 ring-[var(--color-line)]"
      />
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setBroken(true)}
      className="h-11 w-8 shrink-0 rounded-sm object-cover ring-1 ring-[var(--color-line)]"
    />
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="py-2">
      <div className="px-4 pb-1.5 text-2xs font-medium uppercase tracking-[0.16em] text-[var(--color-faint)]">
        {label}
      </div>
      {children}
    </div>
  );
}

export function SearchPanel({
  open,
  initialTerm,
  onClose,
}: {
  open: boolean;
  initialTerm: string;
  onClose: () => void;
}) {
  useUiLang();
  const [term, setTerm] = useState(initialTerm);
  const [cursor, setCursor] = useState(0);
  const debounced = useDebounced(term, 200);
  const settled = useDebounced(term, CATALOGUE_DEBOUNCE);
  const { results, busy } = useSearch(debounced, open);
  const { found, busy: hunting } = useDiscover(settled, open);
  // Keyed by source and identifier rather than by slug: the slug is the
  // server's answer and arrives with the row, but a row the reader clicked
  // twice in half a second has to be recognised before any answer comes back.
  const [asked, setAsked] = useState<Record<string, "adding" | "queued" | "failed">>({});
  const input = useRef<HTMLInputElement | null>(null);
  const list = useRef<HTMLDivElement | null>(null);

  // The shelf behind keeps its place while the overlay is up.
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    setTerm(initialTerm);
    setCursor(0);
    // The panel exists to be typed into, so it takes focus the moment it opens.
    const timer = window.setTimeout(() => input.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open, initialTerm]);

  const rows = useMemo<Row[]>(() => {
    const books: Row[] = results.books.map((book) => ({
      kind: "book",
      book,
      target: href({ name: "book", id: book.id }),
    }));
    const hits: Row[] = results.hits.map((hit) => ({
      kind: "hit",
      hit,
      target: passageHref(hit.book_id, hit.sentence),
    }));
    const finds: Row[] = found.map((find) => ({ kind: "find", find }));
    return [...books, ...hits, ...finds];
  }, [results, found]);

  useEffect(() => setCursor(0), [rows.length]);

  const go = useCallback(
    (target: string) => {
      onClose();
      location.hash = target;
    },
    [onClose],
  );

  /**
   * Queue one book, and say so before the server has answered.
   *
   * The reader is about to wait three hours; making them wait another second to
   * find out whether the button worked is the wrong second to spend. The row
   * commits to "on its way" on the click and only steps back if the POST
   * refuses, which at that point is a real error worth showing.
   */
  const add = useCallback(async (find: DiscoverResult) => {
    const key = `${find.source}:${find.ident}`;
    setAsked((current) => (current[key] ? current : { ...current, [key]: "adding" }));
    try {
      await api.acquire(find);
      setAsked((current) => ({ ...current, [key]: "queued" }));
      window.dispatchEvent(new CustomEvent("readcommons:queued"));
    } catch {
      setAsked((current) => ({ ...current, [key]: "failed" }));
    }
  }, []);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (rows.length === 0) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setCursor((current) => (current + step + rows.length) % rows.length);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const row = rows[cursor];
      if (!row) return;
      if (row.kind === "find") void add(row.find);
      else go(row.target);
    }
  };

  // Keep the highlighted row in view when the arrows walk past the fold.
  useEffect(() => {
    const node = list.current?.querySelector<HTMLElement>('[data-cursor="true"]');
    node?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  if (!open) return null;

  const short = debounced.trim().length < 2;
  const nothing = !busy && !hunting && !short && rows.length === 0;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center bg-black/70 px-3 pt-[4vh] backdrop-blur-sm sm:px-4 sm:pt-[12vh]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("searchLibrary")}
        onKeyDown={onKeyDown}
        className="flex max-h-[86vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl sm:max-h-[76vh] bg-[var(--color-surface)] shadow-2xl ring-1 ring-[var(--color-line)]"
      >
        {/* The field is focused the instant the panel opens, so the global focus ring
            would draw a box round the whole width for no information. The border of
            the row carries the state instead. */}
        <div className="flex items-center gap-3 border-b border-[var(--color-line)] px-4 transition-colors duration-150 focus-within:border-[var(--color-accent)]">
          <SearchIcon className="h-4 w-4 shrink-0 text-[var(--color-faint)]" />
          <input
            ref={input}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder={t("searchPlaceholder")}
            aria-label={t("searchPlaceholder")}
            role="combobox"
            aria-expanded={rows.length > 0}
            aria-controls="search-results"
            className="w-full bg-transparent py-4 text-base coarse:min-h-11 text-[var(--color-ink)] placeholder:text-[var(--color-faint)] focus:outline-none focus-visible:outline-none!"
          />
          {(busy || hunting) && <Spinner label={busy ? t("searchBusy") : t("addBusy")} />}
          <button
            onClick={onClose}
            aria-label={t("searchClose")}
            title={t("searchClose")}
            className="tap inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[var(--color-faint)] transition-colors duration-150 hover:bg-[var(--color-raised)] hover:text-[var(--color-ink)]"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>

        <div id="search-results" ref={list} className="min-h-0 flex-1 overflow-y-auto">
          {short && (
            <p className="px-4 py-10 text-center text-xs text-[var(--color-muted)]">
              {t("searchHint")}
            </p>
          )}
          {nothing && (
            <p className="px-4 py-10 text-center text-sm text-[var(--color-ink)]">
              {t("searchNothing", { term: debounced })}
            </p>
          )}

          {results.books.length > 0 && (
            <Section label={t("searchBooks", { n: results.books.length })}>
              {results.books.map((book, index) => (
                <ResultRow
                  key={`b:${book.id}`}
                  selected={cursor === index}
                  onHover={() => setCursor(index)}
                  target={href({ name: "book", id: book.id })}
                  onOpen={go}
                >
                  <span className="truncate text-sm text-[var(--color-ink)]">{book.title}</span>
                  <span className="truncate text-xs text-[var(--color-muted)]">
                    {[book.author, book.year].filter(Boolean).join(", ")}
                  </span>
                </ResultRow>
              ))}
            </Section>
          )}

          {results.hits.length > 0 && (
            <Section label={t("searchPassages", { n: results.hits.length })}>
              {results.hits.map((hit, index) => {
                const at = results.books.length + index;
                return (
                  <ResultRow
                    key={`h:${hit.book_id}:${hit.chapter}:${index}`}
                    selected={cursor === at}
                    onHover={() => setCursor(at)}
                    target={passageHref(hit.book_id, hit.sentence)}
                    onOpen={go}
                  >
                    <span className="line-clamp-2 text-sm leading-relaxed text-[var(--color-ink)]">
                      {renderSnippet(hit.snippet)}
                    </span>
                    <span className="truncate text-xs text-[var(--color-faint)]">
                      {hit.title}
                      {hit.author ? ` · ${hit.author}` : ""} · {t("searchChapter", { n: hit.chapter + 1 })}
                    </span>
                  </ResultRow>
                );
              })}
            </Section>
          )}

          {found.length > 0 && (
            <Section label={t("addSection", { n: found.length })}>
              <p className="px-4 pb-2 text-xs leading-relaxed text-[var(--color-faint)]">
                {t("addHint")}
              </p>
              {found.map((find, index) => {
                const at = results.books.length + results.hits.length + index;
                const key = `${find.source}:${find.ident}`;
                const state = asked[key];
                const on = find.owned || find.queued || state === "queued";
                return (
                  <div
                    key={key}
                    data-cursor={cursor === at ? "true" : undefined}
                    onMouseEnter={() => setCursor(at)}
                    className={`flex items-center gap-3 px-4 py-2 ${
                      cursor === at ? "bg-[var(--color-raised)]" : ""
                    }`}
                  >
                    <Thumb src={find.cover_url} alt="" />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm text-[var(--color-ink)]">{find.title}</span>
                      <span className="truncate text-xs text-[var(--color-faint)]">
                        {[
                          find.author,
                          find.year,
                          t(SOURCE_LABEL[find.source] ?? "addSourceArchive"),
                          find.has_scan ? t("addScan") : t("addNoScan"),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                    {find.owned ? (
                      <span className="shrink-0 text-xs text-[var(--color-faint)]">
                        {t("addOwned")}
                      </span>
                    ) : (
                      <button
                        type="button"
                        disabled={on || state === "adding"}
                        onClick={() => void add(find)}
                        className="tap shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium coarse:min-h-11 transition-colors duration-150 disabled:cursor-default disabled:bg-transparent disabled:text-[var(--color-faint)] bg-[var(--color-accent-soft)] text-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-[var(--color-surface)]"
                      >
                        {on
                          ? t("addQueued")
                          : state === "adding"
                            ? t("addAdding")
                            : state === "failed"
                              ? t("prepRetry")
                              : t("addButton")}
                      </button>
                    )}
                  </div>
                );
              })}
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultRow({
  selected,
  target,
  onHover,
  onOpen,
  children,
}: {
  selected: boolean;
  target: string;
  onHover: () => void;
  onOpen: (target: string) => void;
  children: React.ReactNode;
}) {
  return (
    <a
      href={target}
      data-cursor={selected ? "true" : undefined}
      aria-selected={selected}
      role="option"
      onMouseMove={onHover}
      onClick={(event) => {
        event.preventDefault();
        onOpen(target);
      }}
      className={
        "tap-row flex flex-col justify-center gap-0.5 px-4 py-2.5 " +
        (selected
          ? "bg-[var(--color-raised)] shadow-[inset_2px_0_0_var(--color-accent)]"
          : "hover:bg-[var(--color-raised)]")
      }
    >
      {children}
    </a>
  );
}
